import { Dimension } from '@pubstack/common/src/analytics/dimension'
import { ServerSideMetricsDetailsByDim } from '@pubstack/common/src/analytics/query'
import { CurrencySymbol } from '@pubstack/common/src/currency'
import { useAtom } from 'jotai'
import { FunctionComponent, useCallback, useState } from 'react'
import { useAnalyticsQueryTanstack } from '~/api/analytics/analytics-query.api'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { WidgetProps } from '~/components/Widget'
import { useBreakdownTabs } from '~/hooks/useBreakdownTabs'
import { AnalyticsBreakdown, useAnalyticsBreakdown } from '~/modules/analytics/AnalyticsBreakdown'
import { serverSideBidCpm, serverSideBidRate, serverSideBidRequests, serverSideBidTimeoutRate, serverSideImpressionRevenue } from '~/modules/analytics/common/formulas'
import { contextState } from '~/state'
import { WithClassName } from '~/types/utils'
import { onBreakdownRowClick } from '~/utils/analytics'
import { ANALYTICS_TOOLTIPS } from '~/utils/constants'

type ServerSideDetailBreakdownProps = WithClassName &
  Omit<WidgetProps, 'title' | 'icon' | 'info' | 'refreshTrackingLabel'> & {
    currencySymbol: CurrencySymbol
    dimensions: Dimension[]
    bidderName: string
  }
export const ServerSideDetailBreakdown: FunctionComponent<ServerSideDetailBreakdownProps> = ({ dimensions, currencySymbol, bidderName, ...props }) => {
  const [context, setContext] = useAtom(contextState)
  const [breakdownDimension, setBreakdownDimension] = useState<Dimension>('tagId')
  const analyticsQueryByDim = useAnalyticsQueryTanstack<ServerSideMetricsDetailsByDim>('server-side.metrics.detail.by.dim', dimensions, context, { dimension: breakdownDimension, bidderName })
  const user = useAuthenticatedUser()
  const tabsConfig = useBreakdownTabs(user, ['tagId', 'adUnit', 'country', 'size'])

  const onTabChange = (dimension: Dimension) => {
    setBreakdownDimension(dimension)
  }

  const breakdownProps = useAnalyticsBreakdown<string>({
    currencySymbol,
    rawData: analyticsQueryByDim.data,
    onTabChange,
    tabsConfig,
    defaultTabIndex: 0,
    maxResults: 50,
    getConfigs: useCallback(
      () => [
        [
          {
            displayedAs: 'gauge',
            propertyName: 'name',
            sortLabel: 'Bidder name',
            getRawValue: ({ data }) => (data.bidRequests.percentage !== undefined ? data.bidRequests.percentage : (data.impressionRevenue.percentage ?? 0)),
            max: 1,
          },
          {
            formula: serverSideImpressionRevenue,
            propertyName: 'impressionRevenue',
            tooltip: ANALYTICS_TOOLTIPS.SERVER_SIDE_IMPRESSION_REVENUE,
          },
          {
            formula: serverSideBidRequests,
            propertyName: 'bidRequests',
            tooltip: ANALYTICS_TOOLTIPS.BID_REQUESTS,
          },
          {
            formula: serverSideBidRate,
            propertyName: 'bidRate',
            tooltip: ANALYTICS_TOOLTIPS.BID_RATE,
            status: {
              alert: 5,
              warning: 15,
            },
          },
          {
            formula: serverSideBidCpm,
            propertyName: 'bidCpm',
            tooltip: ANALYTICS_TOOLTIPS.BID_CPM,
          },
          {
            formula: serverSideBidTimeoutRate,
            propertyName: 'bidTimeoutRate',
            tooltip: ANALYTICS_TOOLTIPS.BID_TIMEOUT_RATE,
            status: {
              alert: 10,
              warning: 5,
            },
          },
        ],
      ],
      []
    ),
    getColumnsConfigs: useCallback(() => ['impressionRevenue', 'bidRequests', 'bidRate', 'bidCpm', 'hbWinRate', 'bidTimeoutRate'], []),
    defaultSort: 'bidRequests',
  })
  return (
    <AnalyticsBreakdown
      isLoading={analyticsQueryByDim.isFetching}
      error={analyticsQueryByDim.isError}
      empty={!analyticsQueryByDim.isLoading && !analyticsQueryByDim.data?.name?.length}
      onRowClick={onBreakdownRowClick(breakdownDimension, setContext, !analyticsQueryByDim.data)}
      onSortChange={() => {}}
      refreshTrackingLabel={'ana-server-side-detail-breakdown-refresh'}
      {...props}
      {...breakdownProps}
    />
  )
}
